import React, { useEffect, useState } from 'react';
import { X, Type, Image as ImageIcon, SlidersHorizontal } from 'lucide-react';
import ThemeSettings from './ThemeSettings';
import { LYRICS_PER_SLIDE_OPTIONS, SCRIPTURE_SLIDE_SIZES, DEFAULT_SCRIPTURE_SLIDE_CHARS } from '../lib/slides';
import { textEffectCss, DEFAULT_TEXT_EFFECT } from '../display/themes';

const FONTS = [
  'Arial', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana', 'Trebuchet MS',
  'Impact', 'Comic Sans MS', 'Palatino', 'Garamond', 'Bookman', 'Tahoma',
];

const TABS = [
  { id: 'text', label: 'Text & slides', Icon: Type },
  { id: 'background', label: 'Background', Icon: ImageIcon },
  { id: 'general', label: 'General', Icon: SlidersHorizontal },
];

/**
 * Display settings as a centred panel with three tabs instead of one long flyout.
 * Changes apply to the congregation screen straight away, so there is no Save button.
 */
const SettingsPanel = ({ settings, setSettings, updateSettings, onClose, dm, t }) => {
  const [tab, setTab] = useState('text');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const label = `block text-sm font-semibold mb-1.5 ${t.textPrimary}`;
  const hint = `text-xs mt-1.5 ${t.textMuted}`;
  const control = `w-full p-2.5 text-sm rounded-lg border focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none ${t.selectBg}`;
  const slider = 'w-full h-2 bg-blue-950 rounded-lg appearance-none cursor-pointer accent-amber-500';
  const tabBtn = (active) => `flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-all ${active
    ? 'border-amber-400 text-amber-400'
    : `border-transparent ${t.textSecondary} hover:text-amber-300`}`;
  const effect = { ...DEFAULT_TEXT_EFFECT, ...settings.textEffect };
  const slideChars = settings.scriptureSlideChars === undefined ? DEFAULT_SCRIPTURE_SLIDE_CHARS : settings.scriptureSlideChars;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-label="Display settings"
        className={`w-full max-w-xl max-h-[calc(100dvh-2rem)] flex flex-col rounded-2xl border shadow-2xl ${t.dropdownBg}`}
      >
        <div className="flex items-center justify-between px-5 pt-4">
          <div>
            <h2 className={`text-lg font-bold ${t.textPrimary}`}>Display settings</h2>
            <p className={`text-xs ${t.textMuted}`}>Changes show on the congregation screen straight away.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className={`p-2 rounded-lg transition-all ${t.rowHover}`}>
            <X className={`w-5 h-5 ${t.textSecondary}`} />
          </button>
        </div>

        <div className={`flex gap-1 px-4 mt-3 border-b ${t.divider}`} role="tablist">
          {TABS.map(({ id, label: text, Icon }) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} className={tabBtn(tab === id)}>
              <Icon className="w-4 h-4" /> {text}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {tab === 'text' && (
            <>
              <div
                data-testid="text-sample"
                className="rounded-xl h-24 flex items-center justify-center overflow-hidden px-4 text-center"
                style={{
                  background: settings.backgroundColor,
                  color: settings.fontColor,
                  fontFamily: settings.fontFamily,
                  // The sample is a scaled-down hint, not the real size.
                  fontSize: Math.max(14, Math.min(34, settings.fontSize / 2.4)),
                  ...textEffectCss(effect),
                }}
              >
                For God so loved the world
              </div>

              <div>
                <label className={label}>Font Size: {settings.fontSize}px</label>
                <input
                  type="range" min="24" max="96" value={settings.fontSize}
                  onChange={(e) => updateSettings({ ...settings, fontSize: parseInt(e.target.value) })}
                  className={slider}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end">
                <div>
                  <label className={label} htmlFor="font-family">Font</label>
                  <select
                    id="font-family"
                    value={settings.fontFamily}
                    onChange={(e) => setSettings({ ...settings, fontFamily: e.target.value })}
                    className={control}
                  >
                    {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="text-color">Text colour</label>
                  <input
                    id="text-color"
                    type="color"
                    value={settings.fontColor}
                    onChange={(e) => setSettings({ ...settings, fontColor: e.target.value })}
                    className={`w-full sm:w-20 h-10 rounded-lg cursor-pointer border ${dm ? 'border-slate-700' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className={`rounded-xl border p-4 space-y-4 ${t.divider}`}>
                <div>
                  <label className={label} htmlFor="lyrics-per-slide">Lyrics per slide</label>
                  <select
                    id="lyrics-per-slide"
                    value={settings.lyricsPerSlide || 1}
                    onChange={(e) => updateSettings({ ...settings, lyricsPerSlide: parseInt(e.target.value) })}
                    className={control}
                  >
                    {LYRICS_PER_SLIDE_OPTIONS.map(n => (
                      <option key={n} value={n}>{n === 1 ? '1 line (one at a time)' : `${n} lines`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="scripture-slide-size">Long Bible passages</label>
                  <select
                    id="scripture-slide-size"
                    value={slideChars}
                    onChange={(e) => updateSettings({ ...settings, scriptureSlideChars: parseInt(e.target.value) })}
                    className={control}
                  >
                    {SCRIPTURE_SLIDE_SIZES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <p className={hint}>While live, use the ↑ ↓ keys to step through the slides.</p>
                </div>
              </div>
            </>
          )}

          {tab === 'background' && (
            <ThemeSettings settings={settings} onChange={updateSettings} dm={dm} t={t} />
          )}

          {tab === 'general' && (
            <>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className={`text-sm font-semibold ${t.textPrimary}`} id="dark-mode-label">Dark mode</p>
                  <p className={`text-xs ${t.textMuted}`}>Only changes this control screen, not the projector.</p>
                </div>
                <button
                  role="switch"
                  aria-checked={!!settings.darkMode}
                  aria-labelledby="dark-mode-label"
                  onClick={() => setSettings({ ...settings, darkMode: !settings.darkMode })}
                  className={`relative shrink-0 w-12 h-6 rounded-full transition-all ${
                    dm ? 'bg-gradient-to-r from-blue-600 to-amber-500' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${dm ? 'translate-x-6' : ''}`} />
                </button>
              </div>
              <p className={`text-sm ${t.textSecondary}`}>
                The Bible translation is chosen from <strong>Bible Version</strong> in the left menu.
                It is currently <strong>{settings.bibleVersion}</strong>.
              </p>
            </>
          )}
        </div>

        <div className={`px-5 py-3 border-t flex justify-end ${t.divider}`}>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-brand-600 to-brand-500 text-white hover:from-brand-500 hover:to-blue-400"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
